import * as Sentry from "@sentry/react";
const { VITE_ENV = "", VITE_API_URL = "" } = import.meta.env;

const apiRegex = new RegExp(`^${VITE_API_URL.replaceAll(".", "\\.")}`);
const whitelistedEnvs = ["release", "dev"];

const orgSlug = "encamp-98d563ed0";

export default function configureSentry() {
  if (!whitelistedEnvs.includes(VITE_ENV)) {
    // Don't initialize Sentry in sandboxes or local dev
    return;
  }
  Sentry.init({
    dsn: "https://5fe24cc356020a1eac95b574b369cde3@o4505909205729280.ingest.sentry.io/4505909471084544",
    integrations: [
      Sentry.replayIntegration({
        networkDetailAllowUrls: [apiRegex],
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserTracingIntegration({
        instrumentPageLoad: false,
        instrumentNavigation: false,
        traceFetch: true,
        traceXHR: false,
        shouldCreateSpanForRequest: () => false,
      }),
    ],
    tracePropagationTargets: [apiRegex],

    // Session Replay — 100% in release, 10% in other environments
    replaysSessionSampleRate: VITE_ENV === "release" ? 1.0 : 0.1,
    // Always capture replays for sessions where errors occur
    replaysOnErrorSampleRate: 1.0,

    environment: VITE_ENV,
  });
}

export function configureSentryUser({
  user,
  tenantName,
  partnerTenantName,
  tenantId,
  partnerTenantId,
}: {
  user: Sentry.User | null;
  tenantName?: string;
  partnerTenantName?: string;
  tenantId?: string;
  partnerTenantId?: string;
}) {
  if (!whitelistedEnvs.includes(VITE_ENV)) {
    return;
  }
  Sentry.setUser({ ...user });
  if (tenantName) Sentry.setTag("tenant", tenantName);
  if (partnerTenantName) Sentry.setTag("partner", partnerTenantName);
  if (tenantId) Sentry.setTag("tenantId", tenantId);
  if (partnerTenantId) Sentry.setTag("partnerTenantId", partnerTenantId);
}

/**
 * Force flush the current Sentry Replay buffer to ensure data is sent to Sentry.
 * Even if a replay ID exists, the session may be in buffer mode (waiting for an error)
 * and not actually sending data to Sentry. Calling flush() ensures the current
 * replay data is sent immediately.
 */
export function forceStartReplayAndGetLink(): string | undefined {
  if (!whitelistedEnvs.includes(VITE_ENV)) {
    return undefined;
  }
  try {
    const replay = Sentry.getReplay();
    if (!replay) {
      return undefined;
    }

    // Flush the current replay buffer to ensure data is sent to Sentry.
    // This is important when the session is in buffer mode (not sampled for
    //   session recording, but buffered for potential future errors)
    // We don't need to await the flush since we don't rely on the result.
    replay.flush();

    const replayId = replay.getReplayId();
    if (!replayId) {
      return undefined;
    }

    return `https://sentry.io/organizations/${orgSlug}/replays/?query=${encodeURIComponent(
      `id:${replayId}`
    )}`;
  } catch {
    return undefined;
  }
}
